import { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ValidationUtils } from "../lib/validation";

/**
 * Custom hook for form validation
 * Provides real-time validation, error handling, and form state management
 */
export const useValidation = (initialData = {}, validationSchema = null) => {
  const [data, setData] = useState(initialData);
  const [originalData, setOriginalData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);


  // Debounced validation function
  const debouncedValidate = useCallback(
    ValidationUtils.debounce((fieldName, value, schema) => {
      if (schema) {
        const fieldSchema = { [fieldName]: schema.rules[fieldName] || [] };
        const fieldData = { [fieldName]: value };

        const result = ValidationUtils.validateField(value, fieldSchema[fieldName]);

        setErrors((prev) => ({
          ...prev,
          [fieldName]: result,
        }));
      }
      setIsValidating(false);
    }, 300),
    []
  );

  // Validate single field
  const validateField = useCallback(
    (fieldName, value) => {
      if (validationSchema && validationSchema.rules[fieldName]) {
        setIsValidating(true);
        debouncedValidate(fieldName, value, validationSchema);
      }
    },
    [validationSchema, debouncedValidate]
  );

  // Validate entire form
  const validateForm = useCallback(() => {
    if (!validationSchema) return { isValid: true, errors: {} };

    const result = validationSchema.validate(data);
    setErrors(result.errors);
    setIsValid(result.isValid);

    return result;
  }, [data, validationSchema]);

  // Update field value
  const updateField = useCallback(
    (fieldName, value) => {
      setData((prev) => ({
        ...prev,
        [fieldName]: value,
      }));

      // Marquer le formulaire comme initialisé lors de la première modification
      if (!isInitialized) {
        setIsInitialized(true);
      }

      // Validate field if it has been touched
      if (touched[fieldName]) {
        validateField(fieldName, value);
      }
    },
    [touched, validateField, isInitialized]
  );

  // Handle field blur (mark as touched)
  const handleBlur = useCallback(
    (fieldName) => {
      setTouched((prev) => ({
        ...prev,
        [fieldName]: true,
      }));

      // Validate field on blur
      validateField(fieldName, data[fieldName]);
    },
    [data, validateField]
  );

  // Handle field focus (clear error if exists)
  const handleFocus = useCallback(
    (fieldName) => {
      if (errors[fieldName]) {
        setErrors((prev) => ({
          ...prev,
          [fieldName]: null,
        }));
      }
    },
    [errors]
  );

  // Reset form
  const resetForm = useCallback(
    (newData = initialData) => {
      // ⚠️ IMPORTANT: Ne pas normaliser ici car les données viennent déjà normalisées
      setData(newData);
      setOriginalData(newData); // Reset original data too
      setErrors({});
      setTouched({});
      setIsValid(true);

      // Remettre l'état d'initialisation à jour
      setTimeout(() => {
        setIsInitialized(true);
      }, 100);
    },
    [initialData]
  );

  // Set form data
  const setFormData = useCallback((newData) => {
    // ⚠️ IMPORTANT: Ne pas normaliser ici car les données viennent déjà normalisées
    // Cela évite les double-normalisations qui causent des incohérences
    setData(newData);
    setOriginalData(newData); // Update original data when setting new data

    // ✅ SOLUTION FINALE: Marquer le formulaire comme initialisé après un délai
    setTimeout(() => {
      setIsInitialized(true);
    }, 100);
  }, []);

  // Calculate if there are unsaved changes using useMemo for performance
  const hasUnsavedChanges = useMemo(() => {
    // ⚠️ SOLUTION FINALE: Ne détecter des changements que si le formulaire est initialisé
    if (!isInitialized) {
      return false;
    }

    // Normalize data for comparison (convert undefined to null, ensure consistent types)
    const normalizeForComparison = (obj) => {
      const normalized = {};
      Object.keys(obj).forEach((key) => {
        let value = obj[key];
        if (value === undefined) value = "";
        if (value === null) value = "";
        if (typeof value === "number") value = String(value);
        normalized[key] = value;
      });
      return normalized;
    };

    const normalizedData = normalizeForComparison(data);
    const normalizedOriginal = normalizeForComparison(originalData);

    // Compare the normalized objects
    const dataStr = JSON.stringify(normalizedData);
    const originalDataStr = JSON.stringify(normalizedOriginal);

    return dataStr !== originalDataStr;
  }, [data, originalData, isInitialized]);

  // Get field error
  const getFieldError = useCallback(
    (fieldName) => {
      return touched[fieldName] ? errors[fieldName] : null;
    },
    [errors, touched]
  );

  // Check if field is valid
  const isFieldValid = useCallback(
    (fieldName) => {
      return !touched[fieldName] || !errors[fieldName];
    },
    [errors, touched]
  );

  // Get field props for form components
  const getFieldProps = useCallback(
    (fieldName) => ({
      value: data[fieldName] || "",
      onChange: (e) => {
        const value = e.target ? e.target.value : e;
        updateField(fieldName, value);
      },
      onBlur: () => handleBlur(fieldName),
      onFocus: () => handleFocus(fieldName),
      error: getFieldError(fieldName),
      isValid: isFieldValid(fieldName),
    }),
    [data, updateField, handleBlur, handleFocus, getFieldError, isFieldValid]
  );

  // Update validation when data changes
  /*useEffect(() => {
    if (validationSchema) {
      const result = validationSchema.validate(data);
      setIsValid(result.isValid);
      setErrors(result.errors); // Also update errors to keep them in sync
    }
  }, [data, validationSchema]);*/

  return {
    // Data
    data,
    errors,
    touched,
    isValid,
    isValidating,
    hasUnsavedChanges,
    isInitialized, // Exposer l'état d'initialisation pour debug

    // Methods
    updateField,
    validateField,
    validateForm,
    handleBlur,
    handleFocus,
    resetForm,
    setFormData,
    getFieldError,
    isFieldValid,
    getFieldProps,
  };
};

/**
 * Hook for real-time field validation
 */
export const useFieldValidation = (initialValue = "", validators = []) => {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const debouncedValidate = useCallback(
    ValidationUtils.debounce((val) => {
      const result = ValidationUtils.validateField(val, validators);
      setError(result);
      setIsValidating(false);
    }, 300),
    [validators]
  );

  const validate = useCallback(
    (val) => {
      setIsValidating(true);
      debouncedValidate(val);
    },
    [debouncedValidate]
  );

  const handleChange = useCallback(
    (newValue) => {
      setValue(newValue);
      if (touched) {
        validate(newValue);
      }
    },
    [touched, validate]
  );

  const handleBlur = useCallback(() => {
    setTouched(true);
    validate(value);
  }, [value, validate]);

  const handleFocus = useCallback(() => {
    if (error) {
      setError(null);
    }
  }, [error]);

  const reset = useCallback(
    (newValue = initialValue) => {
      setValue(newValue);
      setError(null);
      setTouched(false);
      setIsValidating(false);
    },
    [initialValue]
  );

  return {
    value,
    error: touched ? error : null,
    touched,
    isValidating,
    isValid: !touched || !error,
    setValue: handleChange,
    onBlur: handleBlur,
    onFocus: handleFocus,
    validate: () => validate(value),
    reset,
  };
};

/**
 * Hook for password strength validation
 */
export const usePasswordValidation = (initialValue = "") => {
  const { t } = useTranslation();
  const [password, setPassword] = useState(initialValue);
  const [strength, setStrength] = useState({ strength: 0, label: t("validation.pwNone"), color: "gray" });
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);

  const updatePassword = useCallback(
    (value) => {
      setPassword(value);
      const strengthResult = ValidationUtils.getPasswordStrength(value);
      setStrength(strengthResult);

      if (touched) {
        const validators = [(val) => (val.length < 8 ? t("validation.minLength", { min: 8 }) : null)];
        const result = ValidationUtils.validateField(value, validators);
        setError(result);
      }
    },
    [t, touched]
  );

  const handleBlur = useCallback(() => {
    setTouched(true);
    const validators = [(val) => (val.length < 8 ? t("validation.minLength", { min: 8 }) : null)];
    const result = ValidationUtils.validateField(password, validators);
    setError(result);
  }, [password, t]);

  return {
    password,
    strength,
    error: touched ? error : null,
    touched,
    isValid: !touched || !error,
    setPassword: updatePassword,
    onBlur: handleBlur,
    reset: () => {
      setPassword(initialValue);
      setStrength({ strength: 0, label: t("validation.pwNone"), color: "gray" });
      setError(null);
      setTouched(false);
    },
  };
};
